import mixpanel from 'mixpanel-browser';
import { getEnvironmentName, isTelemetryEnabled } from './env';

let isInitialized = false;

// Module-level state for authenticated user (Descope ID, email, and name)
// Used to include user_id, email, and name in GTM events when user is authenticated
let authenticatedUserId: string | undefined;
let authenticatedUserEmail: string | undefined;
let authenticatedUserName: string | undefined;

/**
 * Set the authenticated user for GTM event tracking.
 * Called when user logs in (with Descope user ID, email, and name) or logs out (undefined).
 * This enables the unified analytics layer to include user_id, email, and name in GTM events.
 */
export function setAuthenticatedUser(
  userId: string | undefined,
  email?: string,
  name?: string
): void {
  authenticatedUserId = userId;
  authenticatedUserEmail = email;
  authenticatedUserName = name;
}

/**
 * Mixpanel runs a broken `_.isFunction` that only accepts values whose string form
 * starts with "function" (arrow/async class methods can fail). Keep this as a
 * top-level `function` declaration so production bundles stay valid.
 */
function mixpanelBeforeTrackHook(
  eventName: string,
  properties: Record<string, unknown> | undefined
): [string, Record<string, unknown>] {
  const props = { ...(properties ?? {}) };
  if (!props.environment) {
    props.environment = getEnvironmentName();
  }
  return [eventName, props];
}

export function initMixpanel(): void {
  if (isInitialized) {
    return;
  }
  const token = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
  const envName = getEnvironmentName();
  const enabled = !!token && isTelemetryEnabled();

  if (!enabled) return;

  type InitOpts = Parameters<typeof mixpanel.init>[1];
  const opts: InitOpts & Record<string, unknown> = {
    debug: import.meta.env.DEV,
    track_pageview: false, // We manually track pageviews
    persistence: 'localStorage',
    // Hooks allow us to intercept every event before it leaves the browser.
    // This is the most robust way to ensure 'environment' is present on ALL events,
    // including those triggered by autocapture which might race against mixpanel.register().
    hooks: {
      before_track: mixpanelBeforeTrackHook,
    },
  };

  // Optional region override
  const explicitApiHost = import.meta.env.VITE_MIXPANEL_API_HOST as
    | string
    | undefined;
  const explicitDecideHost = import.meta.env.VITE_MIXPANEL_DECIDE_HOST as
    | string
    | undefined;
  const explicitRecordingHost = import.meta.env.VITE_MIXPANEL_RECORDING_HOST as
    | string
    | undefined;

  const useTunnel = String(import.meta.env.VITE_MIXPANEL_USE_TUNNEL) === 'true';
  const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) || '';

  if (useTunnel) {
    // Configure tunnel endpoints
    // Note: Ad blockers block URLs containing "track" - use api_routes to rename paths
    // /mixpanel/events instead of /mixpanel/track
    // The backend tunnel no longer requires authentication, so standard
    // sendBeacon transport (default) works fine and is preferred for performance.
    (opts as Record<string, unknown>).api_host = import.meta.env.DEV
      ? '/mixpanel'
      : apiBase
        ? `${apiBase}/mixpanel`
        : '/mixpanel';
    // Rename "track" to "events" to avoid ad-blocker detection
    (opts as Record<string, unknown>).api_routes = {
      track: 'events',
      engage: 'engage',
      groups: 'groups',
      record: 'record',
    };
    (opts as Record<string, unknown>).decide_host = import.meta.env.DEV
      ? '/mixpanel-decide'
      : apiBase
        ? `${apiBase}/mixpanel-decide`
        : '/mixpanel-decide';
    (opts as Record<string, unknown>).recording_host = import.meta.env.DEV
      ? '/mixpanel-record'
      : apiBase
        ? `${apiBase}/mixpanel-record`
        : '/mixpanel-record';
  } else {
    if (explicitApiHost)
      (opts as Record<string, unknown>).api_host = explicitApiHost;
    if (explicitDecideHost)
      (opts as Record<string, unknown>).decide_host = explicitDecideHost;
    if (explicitRecordingHost)
      (opts as Record<string, unknown>).recording_host = explicitRecordingHost;
  }

  if (String(import.meta.env.VITE_MIXPANEL_AUTOCAPTURE) === 'true') {
    // Supported by latest Mixpanel SDKs; kept loose-typed via index signature
    (opts as Record<string, unknown>).autocapture = true;
  }
  const sessionsPercent = Number(
    (import.meta.env.VITE_MIXPANEL_RECORD_SESSIONS_PERCENT as
      | string
      | undefined) ?? '0'
  );
  if (!Number.isNaN(sessionsPercent) && sessionsPercent > 0) {
    (opts as Record<string, unknown>).record_sessions_percent = sessionsPercent;
  }

  // Use the loaded callback to register super properties before autocapture events fire
  // This prevents a race condition where autocapture events might fire before register()
  (opts as Record<string, unknown>).loaded = (mp: typeof mixpanel) => {
    mp.register({ environment: envName });
  };

  mixpanel.init(token, opts);
  isInitialized = true;
}

/**
 * Track an event to both GTM and Mixpanel.
 *
 * GTM and Mixpanel are independent: GTM receives events even if Mixpanel
 * is not initialized. This ensures marketing/analytics tags work regardless
 * of product analytics configuration.
 *
 * Note: Mixpanel autocapture events (if enabled) are NOT sent to GTM.
 * Only explicit trackEvent() calls are mirrored to both platforms.
 */
export function trackEvent(
  event: string,
  properties?: Record<string, unknown>
): void {
  const envName = getEnvironmentName();
  const allProperties = { ...properties, environment: envName };

  // Push to GTM (independent of Mixpanel)
  // GTM may be initialized even if Mixpanel is not
  try {
    if (window.dataLayer) {
      window.dataLayer.push({
        // Spread properties first, then apply explicit fields:
        // - event: always uses the function parameter (prevents accidental override)
        // - user_id/email/user_name priority: 1) authenticated user, 2) explicitly passed in properties
        //   (e.g. first_time_login before auth is set), 3) null to clear previous value
        //   (GTM values persist until explicitly overwritten, so we always set user_id/email/user_name)
        ...allProperties,
        user_id:
          authenticatedUserId ??
          (properties?.user_id as string | null | undefined) ??
          null,
        email:
          authenticatedUserEmail ??
          (properties?.email as string | null | undefined) ??
          null,
        user_name:
          authenticatedUserName ??
          (properties?.user_name as string | null | undefined) ??
          null,
        event,
      });
    }
  } catch (e) {
    // Swallow GTM errors - should not break the app
    if (import.meta.env.DEV) {
      console.warn('[GTM] Failed to push event:', e);
    }
  }

  // Push to Mixpanel (if initialized).
  // Mixpanel's `_.isFunction` heuristic and a few internal call sites can
  // throw synchronously on unexpected input. Telemetry must never break the
  // caller's success contract — every consumer treats `trackEvent` as
  // fire-and-forget — so we mirror the GTM try/catch above.
  if (isInitialized) {
    try {
      mixpanel.track(event, allProperties);
    } catch (e) {
      if (import.meta.env.DEV) {
        console.warn('[Mixpanel] Failed to track event:', e);
      }
    }
  }
}

export function mixpanelIdentifyUser(
  id?: string,
  props?: Record<string, unknown>,
  name?: string
): void {
  if (!isInitialized || !id) return;
  mixpanel.identify(id);

  // Build user properties, adding $name if provided
  const userProps: Record<string, unknown> = { ...props };
  if (name) {
    userProps.$name = name;
  }

  if (Object.keys(userProps).length) {
    mixpanel.people.set(userProps as Record<string, string | number | boolean>);
  }
}

export function mixpanelResetIdentity(): void {
  if (!isInitialized) return;
  mixpanel.reset();
}

export function trackPageview(pathname?: string): void {
  trackEvent('page_view', { pathname: pathname ?? window.location.pathname });
}
